import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, Search, CheckCircle2, X, Users, Building2, AlertTriangle, UserPlus, Home, RefreshCw, Clock, Briefcase, XCircle, Globe, FileText, ListChecks, Layers, Replace } from 'lucide-react';
import { WORKFLOW_STEPS } from '@/utils/workflowSteps';
import { getWfPickerColor } from '@/utils/statusConfig';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const WF_ICONS = {
  new_residence: Home, renewal: RefreshCw, temp_work_permit: Clock, partial_work: Briefcase,
  cancellation: XCircle, cancellation_temp_6m: XCircle, new_residence_outside: Globe,
  cancellation_abroad: Globe, cancellation_temp_2y: XCircle, establishment_transactions: Building2,
};

// Price memory
const PRICE_KEY = 'azizi_plan_prices';
const loadPrices = () => { try { return JSON.parse(localStorage.getItem(PRICE_KEY) || '{}'); } catch { return {}; } };
const savePrices = (wfKey, steps) => {
  const all = loadPrices();
  if (!all[wfKey]) all[wfKey] = {};
  steps.forEach(s => { if (parseFloat(s.price) > 0) all[wfKey][s.sub_type] = parseFloat(s.price); });
  localStorage.setItem(PRICE_KEY, JSON.stringify(all));
};
const getPriceFor = (wfKey, subType) => loadPrices()[wfKey]?.[subType] || 0;

// Indent levels for staircase (RTL: margin-right increases)
const INDENT = ['mr-0', 'mr-8', 'mr-16', 'mr-20'];
const INDENT_LTR = ['ml-0', 'ml-8', 'ml-16', 'ml-20'];

// Step color themes per workflow type (full Tailwind class strings for purge safety)
const STEP_THEMES = {
  new_residence: { chip: 'bg-emerald-900 shadow-emerald-400/20 ring-emerald-400/40', hover: 'hover:border-emerald-500 hover:text-emerald-800 hover:bg-emerald-50', optHover: 'hover:border-emerald-400 hover:text-emerald-700', check: 'text-emerald-300', bar: 'bg-emerald-700', wrap: 'bg-emerald-950/5 border-emerald-200/40', row: 'border-emerald-100', addTxt: 'text-emerald-600', addBg: 'hover:bg-emerald-50', pickBorder: 'border-emerald-400', pickBg: 'bg-emerald-50', pickIcon: 'text-emerald-600', pickText: 'text-emerald-800', mid: 'bg-emerald-700' },
  renewal: { chip: 'bg-blue-900 shadow-blue-400/20 ring-blue-400/40', hover: 'hover:border-blue-500 hover:text-blue-800 hover:bg-blue-50', optHover: 'hover:border-blue-400 hover:text-blue-700', check: 'text-blue-300', bar: 'bg-blue-700', wrap: 'bg-blue-950/5 border-blue-200/40', row: 'border-blue-100', addTxt: 'text-blue-600', addBg: 'hover:bg-blue-50', pickBorder: 'border-blue-400', pickBg: 'bg-blue-50', pickIcon: 'text-blue-600', pickText: 'text-blue-800', mid: 'bg-blue-700' },
  temp_work_permit: { chip: 'bg-violet-900 shadow-violet-400/20 ring-violet-400/40', hover: 'hover:border-violet-500 hover:text-violet-800 hover:bg-violet-50', optHover: 'hover:border-violet-400 hover:text-violet-700', check: 'text-violet-300', bar: 'bg-violet-700', wrap: 'bg-violet-950/5 border-violet-200/40', row: 'border-violet-100', addTxt: 'text-violet-600', addBg: 'hover:bg-violet-50', pickBorder: 'border-violet-400', pickBg: 'bg-violet-50', pickIcon: 'text-violet-600', pickText: 'text-violet-800', mid: 'bg-violet-700' },
  partial_work: { chip: 'bg-violet-900 shadow-violet-400/20 ring-violet-400/40', hover: 'hover:border-violet-500 hover:text-violet-800 hover:bg-violet-50', optHover: 'hover:border-violet-400 hover:text-violet-700', check: 'text-violet-300', bar: 'bg-violet-700', wrap: 'bg-violet-950/5 border-violet-200/40', row: 'border-violet-100', addTxt: 'text-violet-600', addBg: 'hover:bg-violet-50', pickBorder: 'border-violet-400', pickBg: 'bg-violet-50', pickIcon: 'text-violet-600', pickText: 'text-violet-800', mid: 'bg-violet-700' },
  gcc_work_permit: { chip: 'bg-lime-900 shadow-lime-400/20 ring-lime-400/40', hover: 'hover:border-lime-500 hover:text-lime-800 hover:bg-lime-50', optHover: 'hover:border-lime-400 hover:text-lime-700', check: 'text-lime-300', bar: 'bg-lime-700', wrap: 'bg-lime-950/5 border-lime-200/40', row: 'border-lime-100', addTxt: 'text-lime-600', addBg: 'hover:bg-lime-50', pickBorder: 'border-lime-400', pickBg: 'bg-lime-50', pickIcon: 'text-lime-600', pickText: 'text-lime-800', mid: 'bg-lime-700' },
  cancellation: { chip: 'bg-red-900 shadow-red-400/20 ring-red-400/40', hover: 'hover:border-red-500 hover:text-red-800 hover:bg-red-50', optHover: 'hover:border-red-400 hover:text-red-700', check: 'text-red-300', bar: 'bg-red-700', wrap: 'bg-red-950/5 border-red-200/40', row: 'border-red-100', addTxt: 'text-red-600', addBg: 'hover:bg-red-50', pickBorder: 'border-red-400', pickBg: 'bg-red-50', pickIcon: 'text-red-600', pickText: 'text-red-800', mid: 'bg-red-700' },
  cancellation_temp_6m: { chip: 'bg-orange-900 shadow-orange-400/20 ring-orange-400/40', hover: 'hover:border-orange-500 hover:text-orange-800 hover:bg-orange-50', optHover: 'hover:border-orange-400 hover:text-orange-700', check: 'text-orange-300', bar: 'bg-orange-700', wrap: 'bg-orange-950/5 border-orange-200/40', row: 'border-orange-100', addTxt: 'text-orange-600', addBg: 'hover:bg-orange-50', pickBorder: 'border-orange-400', pickBg: 'bg-orange-50', pickIcon: 'text-orange-600', pickText: 'text-orange-800', mid: 'bg-orange-700' },
  new_residence_outside: { chip: 'bg-teal-900 shadow-teal-400/20 ring-teal-400/40', hover: 'hover:border-teal-500 hover:text-teal-800 hover:bg-teal-50', optHover: 'hover:border-teal-400 hover:text-teal-700', check: 'text-teal-300', bar: 'bg-teal-700', wrap: 'bg-teal-950/5 border-teal-200/40', row: 'border-teal-100', addTxt: 'text-teal-600', addBg: 'hover:bg-teal-50', pickBorder: 'border-teal-400', pickBg: 'bg-teal-50', pickIcon: 'text-teal-600', pickText: 'text-teal-800', mid: 'bg-teal-700' },
  cancellation_abroad: { chip: 'bg-rose-900 shadow-rose-400/20 ring-rose-400/40', hover: 'hover:border-rose-500 hover:text-rose-800 hover:bg-rose-50', optHover: 'hover:border-rose-400 hover:text-rose-700', check: 'text-rose-300', bar: 'bg-rose-700', wrap: 'bg-rose-950/5 border-rose-200/40', row: 'border-rose-100', addTxt: 'text-rose-600', addBg: 'hover:bg-rose-50', pickBorder: 'border-rose-400', pickBg: 'bg-rose-50', pickIcon: 'text-rose-600', pickText: 'text-rose-800', mid: 'bg-rose-700' },
  cancellation_temp_2y: { chip: 'bg-amber-900 shadow-amber-400/20 ring-amber-400/40', hover: 'hover:border-amber-500 hover:text-amber-800 hover:bg-amber-50', optHover: 'hover:border-amber-400 hover:text-amber-700', check: 'text-amber-300', bar: 'bg-amber-700', wrap: 'bg-amber-950/5 border-amber-200/40', row: 'border-amber-100', addTxt: 'text-amber-600', addBg: 'hover:bg-amber-50', pickBorder: 'border-amber-400', pickBg: 'bg-amber-50', pickIcon: 'text-amber-600', pickText: 'text-amber-800', mid: 'bg-amber-700' },
  establishment_transactions: { chip: 'bg-indigo-900 shadow-indigo-400/20 ring-indigo-400/40', hover: 'hover:border-indigo-500 hover:text-indigo-800 hover:bg-indigo-50', optHover: 'hover:border-indigo-400 hover:text-indigo-700', check: 'text-indigo-300', bar: 'bg-indigo-700', wrap: 'bg-indigo-950/5 border-indigo-200/40', row: 'border-indigo-100', addTxt: 'text-indigo-600', addBg: 'hover:bg-indigo-50', pickBorder: 'border-indigo-400', pickBg: 'bg-indigo-50', pickIcon: 'text-indigo-600', pickText: 'text-indigo-800', mid: 'bg-indigo-700' },
};
const DEFAULT_THEME = { chip: 'bg-slate-800 shadow-slate-400/20 ring-slate-400/40', hover: 'hover:border-slate-500 hover:text-slate-800 hover:bg-slate-50', optHover: 'hover:border-slate-400 hover:text-slate-700', check: 'text-slate-300', bar: 'bg-slate-600', wrap: 'bg-slate-100/50 border-slate-200/40', row: 'border-slate-100', addTxt: 'text-slate-600', addBg: 'hover:bg-slate-50', pickBorder: 'border-slate-400', pickBg: 'bg-slate-50', pickIcon: 'text-slate-500', pickText: 'text-slate-700', mid: 'bg-slate-600' };
const getStepTheme = (wfKey) => STEP_THEMES[wfKey] || DEFAULT_THEME;

// ── Entity Search ──
const EntitySearch = ({ isRTL, employees, companies, selectedIds, onAdd, onCancel, showCancel }) => {
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('employee');
  const list = tab === 'employee'
    ? employees.filter(e => !search.trim() || e.name?.toLowerCase().includes(search.trim().toLowerCase()))
    : companies.filter(c => !search.trim() || c.name?.toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5 w-fit border border-slate-200">
        <button onClick={() => setTab('employee')} data-testid="search-tab-employee"
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md transition-all ${tab === 'employee' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
          <Users className="w-3.5 h-3.5" /> {isRTL ? 'موظف' : 'Employee'}
        </button>
        <button onClick={() => setTab('company')} data-testid="search-tab-company"
          className={`flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-md transition-all ${tab === 'company' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
          <Building2 className="w-3.5 h-3.5" /> {isRTL ? 'منشأة' : 'Company'}
        </button>
      </div>
      <div className="relative">
        <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 ${isRTL ? 'right-3' : 'left-3'}`} />
        <Input value={search} onChange={e => setSearch(e.target.value)} autoFocus data-testid="entity-search"
          placeholder={tab === 'employee' ? (isRTL ? 'ابحث عن موظف...' : 'Search employee...') : (isRTL ? 'ابحث عن منشأة...' : 'Search company...')}
          className={`h-10 text-sm ${isRTL ? 'pr-10' : 'pl-10'}`} />
      </div>
      <div className="max-h-56 overflow-y-auto space-y-0.5">
        {list.map(item => (
          <button key={item.id} onClick={() => onAdd(item, tab)} data-testid={`entity-option-${item.id}`}
            className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors">
            <div className={`w-8 h-8 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0 ${tab === 'company' ? 'bg-blue-600' : 'bg-[#0F172A]'}`}>
              {tab === 'company' ? <Building2 className="w-3.5 h-3.5" /> : item.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0 text-start">
              <p className="text-sm text-slate-700 font-medium truncate">{item.name}</p>
              {tab === 'employee' && <p className="text-[10px] text-slate-400 truncate">{item.company_name}</p>}
            </div>
          </button>
        ))}
      </div>
      {showCancel && <Button variant="ghost" size="sm" onClick={onCancel} className="text-xs text-slate-400 w-full">{isRTL ? 'إلغاء' : 'Cancel'}</Button>}
    </Card>
  );
};

// ── Staircase Wizard for a single employee ──
const StaircaseWizard = ({ item, index, isRTL, onUpdate, onRemove, onReplaceEmployee, canRemove, collapsed, onExpand, collapsedNumber }) => {
  const [selectedWf, setSelectedWf] = useState(item.selectedWf || null);
  const [selectionMode, setSelectionMode] = useState(item.steps.length > 0 ? 'full' : null);
  const [customStepName, setCustomStepName] = useState('');

  const wfEntries = Object.entries(WORKFLOW_STEPS).filter(([, wf]) => item.planType === 'company' ? wf.company : !wf.company);
  const mainWfs = wfEntries.filter(([k]) => !k.includes('cancel'));
  const cancelWfs = wfEntries.filter(([k]) => k.includes('cancel'));
  const itemTotal = item.steps.reduce((s, st) => s + (parseFloat(st.price) || 0), 0);
  const hasDuplicateWf = selectedWf && item.existingPlans?.active?.some(p => p.main_types?.includes(selectedWf));
  const indent = isRTL ? INDENT : INDENT_LTR;

  const priceFor = (wfKey, subType) => item.priceList?.[wfKey]?.[subType] || getPriceFor(wfKey, subType);

  const selectWf = (wfKey) => {
    setSelectedWf(wfKey);
    setSelectionMode(null);
    onUpdate(index, { selectedWf: wfKey, steps: [] });
  };

  const applyFull = () => {
    const wf = WORKFLOW_STEPS[selectedWf];
    if (!wf) return;
    const steps = wf.steps.filter(s => !s.optional).map(s => ({
      sub_type: s.sub_type, label_ar: s.ar, label_en: s.en, main_type: selectedWf,
      price: priceFor(selectedWf, s.sub_type), is_company: item.planType === 'company'
    }));
    onUpdate(index, { steps });
    setSelectionMode('full');
  };

  const applyPartial = () => {
    onUpdate(index, { steps: [] });
    setSelectionMode('partial');
  };

  const toggleStep = (s) => {
    const exists = item.steps.find(st => st.sub_type === s.sub_type && st.main_type === selectedWf);
    if (exists) {
      onUpdate(index, { steps: item.steps.filter(st => !(st.sub_type === s.sub_type && st.main_type === selectedWf)) });
    } else {
      onUpdate(index, { steps: [...item.steps, {
        sub_type: s.sub_type, label_ar: s.ar, label_en: s.en, main_type: selectedWf,
        price: priceFor(selectedWf, s.sub_type), is_company: item.planType === 'company'
      }] });
    }
  };

  const updatePrice = (idx, price) => {
    const n = [...item.steps]; n[idx].price = price;
    onUpdate(index, { steps: n });
  };

  const removeStep = (idx) => onUpdate(index, { steps: item.steps.filter((_, i) => i !== idx) });

  const addCustomStep = () => {
    if (!customStepName.trim()) return;
    onUpdate(index, { steps: [...item.steps, { sub_type: customStepName, label_ar: customStepName, label_en: customStepName, main_type: selectedWf || '', price: 0, is_company: item.planType === 'company' }] });
    setCustomStepName('');
  };

  return (
    <div className="space-y-3" data-testid={`staircase-${index}`}>

      {/* ── Collapsed Summary View ── */}
      {collapsed && item.steps.length > 0 ? (
        <div className="cursor-pointer rounded-2xl overflow-hidden border border-slate-200 hover:shadow-md transition-shadow" onClick={onExpand}>
          <div className={`flex items-center gap-3 py-3 px-5 ${selectedWf ? getWfPickerColor(selectedWf).dark : 'bg-[#0F172A]'}`}>
            <div className="w-8 h-8 rounded-full bg-white/20 text-white text-sm font-black flex items-center justify-center shrink-0">
              {collapsedNumber}
            </div>
            <div className={`w-10 h-10 rounded-xl text-white text-base font-bold flex items-center justify-center shrink-0 bg-white/15`}>
              {item.planType === 'company' ? <Building2 className="w-5 h-5" /> : item.entity?.name?.charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold text-white truncate">{item.entity?.name}</p>
              {selectedWf && <p className="text-[10px] text-white/50">{isRTL ? WORKFLOW_STEPS[selectedWf]?.ar : WORKFLOW_STEPS[selectedWf]?.en}</p>}
            </div>
            <span className="text-sm font-bold text-white/90" dir="ltr">{itemTotal.toLocaleString()} AED</span>
            {canRemove && (
              <button onClick={(e) => { e.stopPropagation(); onRemove(index); }} className="p-1.5 hover:bg-white/10 rounded-lg text-white/50 hover:text-red-300 shrink-0"><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
          {(() => { const t = selectedWf ? getStepTheme(selectedWf) : DEFAULT_THEME; return (
          <div className={`px-4 py-2.5 ${t.wrap} flex flex-wrap gap-x-4 gap-y-1`}>
            {item.steps.map((step, idx) => (
              <div key={idx} className="flex items-center gap-1.5">
                <div className={`w-1 h-4 rounded-full ${t.bar} shrink-0`} />
                <span className="text-xs font-semibold text-slate-700">{isRTL ? step.label_ar : step.label_en}</span>
                {parseFloat(step.price) > 0 && <span className="text-xs font-bold text-slate-500" dir="ltr">{parseFloat(step.price).toLocaleString()}</span>}
              </div>
            ))}
          </div>
          ); })()}
        </div>
      ) : (
      <>

      {/* ── Level 1: Employee Name (far right in RTL) ── */}
      <div className={`${indent[0]}`}>
        <div className="flex items-center gap-3 py-4 px-5 bg-[#0F172A] rounded-2xl cursor-pointer hover:bg-[#1a2744] transition-colors" onClick={() => onReplaceEmployee(index)}>
          <div className={`w-12 h-12 rounded-xl text-white text-lg font-bold flex items-center justify-center shrink-0 ${item.planType === 'company' ? 'bg-blue-600' : 'bg-[#D4AF37]'}`}>
            {item.planType === 'company' ? <Building2 className="w-6 h-6" /> : item.entity?.name?.charAt(0)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-lg font-bold text-white truncate">{item.entity?.name}</p>
            <p className="text-xs text-white/50 truncate">{item.entity?.company_name}</p>
          </div>
          {itemTotal > 0 && <span className="text-sm font-bold text-[#D4AF37]" dir="ltr">{itemTotal.toLocaleString()} AED</span>}
          {canRemove && (
            <button onClick={(e) => { e.stopPropagation(); onRemove(index); }} className="p-1.5 hover:bg-red-500/20 rounded-lg text-red-400 shrink-0"><Trash2 className="w-4 h-4" /></button>
          )}
        </div>

        {/* Existing plans warning */}
        {item.existingPlans && (item.existingPlans.active?.length > 0 || item.existingPlans.archived?.length > 0) && (
          <div className="mt-1.5 px-5 py-2 bg-amber-50 rounded-xl border border-amber-200 space-y-1">
            <div className="flex items-center gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
              <span className="text-[10px] font-bold text-amber-700">{isRTL ? 'خطط سابقة:' : 'Existing plans:'}</span>
            </div>
            {item.existingPlans.active?.map((p, i) => {
              const typeLabels = (p.main_types || []).map(mt => { const wf = WORKFLOW_STEPS[mt]; return wf ? (isRTL ? wf.ar : wf.en) : mt; }).join('، ');
              return (
                <div key={`a-${i}`} className="flex items-center gap-1.5 flex-wrap" style={{ paddingInlineStart: '20px' }}>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">{isRTL ? 'شغّالة' : 'Active'}</span>
                  {typeLabels && <span className="text-[10px] font-semibold text-slate-700">{typeLabels}</span>}
                  {p.company_name && <span className="text-[9px] text-slate-500">({p.company_name})</span>}
                </div>
              );
            })}
            {item.existingPlans.archived?.map((p, i) => {
              const typeLabels = (p.main_types || []).map(mt => { const wf = WORKFLOW_STEPS[mt]; return wf ? (isRTL ? wf.ar : wf.en) : mt; }).join('، ');
              return (
                <div key={`r-${i}`} className="flex items-center gap-1.5 flex-wrap" style={{ paddingInlineStart: '20px' }}>
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-600 font-semibold">{isRTL ? 'مكتملة' : 'Done'}</span>
                  {typeLabels && <span className="text-[10px] font-semibold text-slate-700">{typeLabels}</span>}
                  {p.company_name && <span className="text-[9px] text-slate-500">({p.company_name})</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Level 2: Plan Type (indented) ── */}
      <div className={`${indent[1]}`}>
        {!selectedWf ? (
          <Card className="p-4 border-slate-200">
            <p className="text-sm font-bold text-slate-700 mb-3">{isRTL ? 'اختر نوع الخطة' : 'Choose Plan Type'}</p>
            {mainWfs.length > 0 && (
              <div className="space-y-2 mb-3">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isRTL ? 'الخدمات' : 'Services'}</p>
                <div className="grid grid-cols-2 gap-2">
                  {mainWfs.map(([key, wf]) => {
                    const Icon = WF_ICONS[key] || FileText;
                    const colors = getWfPickerColor(key);
                    return (
                      <button key={key} onClick={() => selectWf(key)} data-testid={`wf-${index}-${key}`}
                        className={`p-3 rounded-xl border-2 text-center transition-all hover:shadow-md hover:scale-[1.02] ${colors.border} ${colors.bg}`}>
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${colors.text}`} />
                        <p className={`text-[11px] font-bold ${colors.text}`}>{isRTL ? wf.ar : wf.en}</p>
                        <p className="text-[9px] text-slate-400 mt-0.5">{wf.steps.filter(s => !s.optional).length} {isRTL ? 'خطوة' : 'steps'}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {cancelWfs.length > 0 && (
              <div className="space-y-2">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{isRTL ? 'الإلغاءات' : 'Cancellations'}</p>
                <div className="grid grid-cols-2 gap-2">
                  {cancelWfs.map(([key, wf]) => {
                    const Icon = WF_ICONS[key] || FileText;
                    const colors = getWfPickerColor(key);
                    return (
                      <button key={key} onClick={() => selectWf(key)} data-testid={`wf-${index}-${key}`}
                        className={`p-3 rounded-xl border-2 text-center transition-all hover:shadow-md hover:scale-[1.02] ${colors.border} ${colors.bg}`}>
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${colors.text}`} />
                        <p className={`text-[11px] font-bold ${colors.text}`}>{isRTL ? wf.ar : wf.en}</p>
                        <p className="text-[9px] text-slate-400 mt-0.5">{wf.steps.filter(s => !s.optional).length} {isRTL ? 'خطوة' : 'steps'}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </Card>
        ) : (
          <div className={`flex items-center gap-3 px-5 py-3 rounded-2xl cursor-pointer hover:opacity-90 transition-opacity ${getWfPickerColor(selectedWf).dark}`}
            onClick={() => { setSelectedWf(null); setSelectionMode(null); onUpdate(index, { selectedWf: null, steps: [] }); }}>
            {(() => { const Icon = WF_ICONS[selectedWf] || FileText; return <Icon className="w-5 h-5 text-white/80 shrink-0" />; })()}
            <div className="flex-1">
              <p className="text-base font-bold text-white">
                {isRTL ? WORKFLOW_STEPS[selectedWf]?.ar : WORKFLOW_STEPS[selectedWf]?.en}
              </p>
              <p className="text-[10px] text-white/40">{WORKFLOW_STEPS[selectedWf]?.steps.filter(s => !s.optional).length} {isRTL ? 'خطوة أساسية' : 'main steps'}</p>
            </div>
          </div>
        )}

        {/* Duplicate warning */}
        {hasDuplicateWf && (
          <div className="mt-1.5 px-4 py-2 bg-red-50 rounded-xl border border-red-200 flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
            <span className="text-[10px] font-bold text-red-600">
              {isRTL ? 'تنبيه: هذا الموظف عنده خطة من نفس النوع شغّالة!' : 'Warning: Active plan of same type exists!'}
            </span>
          </div>
        )}
      </div>

      {/* ── Level 3: Full or Partial (more indented) ── */}
      {selectedWf && (
        <div className={`${indent[2]}`}>
          {!selectionMode ? (
            (() => { const t = getStepTheme(selectedWf); return (
            <Card className="p-4">
              <p className="text-sm font-semibold text-slate-700 mb-3">{isRTL ? 'كيف تريد إضافة الخطوات؟' : 'How to add steps?'}</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={applyFull} data-testid={`full-plan-${index}`}
                  className={`p-4 rounded-xl border-2 ${t.pickBorder} ${t.pickBg} text-center transition-all hover:shadow-md`}>
                  <Layers className={`w-7 h-7 mx-auto mb-2 ${t.pickIcon}`} />
                  <p className={`text-xs font-bold ${t.pickText}`}>{isRTL ? 'الخطة كاملة' : 'Full Plan'}</p>
                  <p className="text-[9px] text-slate-500 mt-1">
                    {WORKFLOW_STEPS[selectedWf]?.steps.filter(s => !s.optional).length} {isRTL ? 'خطوة' : 'steps'}
                  </p>
                </button>
                <button onClick={applyPartial} data-testid={`partial-plan-${index}`}
                  className="p-4 rounded-xl border-2 border-slate-200 bg-slate-50 text-center transition-all hover:shadow-md hover:border-slate-300">
                  <ListChecks className="w-7 h-7 mx-auto mb-2 text-slate-500" />
                  <p className="text-xs font-bold text-slate-600">{isRTL ? 'خطوات معينة' : 'Specific Steps'}</p>
                  <p className="text-[9px] text-slate-500 mt-1">{isRTL ? 'اختيار يدوي' : 'Manual'}</p>
                </button>
              </div>
            </Card>
            ); })()
          ) : (
            (() => { const t = getStepTheme(selectedWf); return (
            <div className={`flex items-center gap-3 px-5 py-2.5 rounded-2xl cursor-pointer hover:opacity-90 transition-opacity ${t.mid}`}
              onClick={() => { setSelectionMode(null); onUpdate(index, { steps: [] }); }}>
              {selectionMode === 'full' ? <Layers className="w-4 h-4 text-white/80 shrink-0" /> : <ListChecks className="w-4 h-4 text-white/80 shrink-0" />}
              <span className="text-sm font-bold text-white flex-1">
                {selectionMode === 'full' ? (isRTL ? 'الخطة كاملة' : 'Full Plan') : (isRTL ? 'خطوات معينة' : 'Specific Steps')}
              </span>
            </div>
            ); })()
          )}
        </div>
      )}

      {/* ── Level 4: Steps & Prices (most indented) ── */}
      {selectedWf && selectionMode && (
        <div className={`${indent[3]}`}>
          {/* Step selection chips */}
          {(() => { const t = getStepTheme(selectedWf); return (
          <div className="mb-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-bold text-slate-500">{isRTL ? 'اختر الخطوات' : 'Select Steps'}</p>
              {selectionMode === 'partial' && item.steps.length === 0 && (
                <button onClick={applyFull} className="text-[10px] text-blue-600 hover:text-blue-700 font-medium" data-testid={`select-all-${index}`}>
                  {isRTL ? 'اختيار الكل' : 'Select All'}
                </button>
              )}
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {WORKFLOW_STEPS[selectedWf]?.steps.map(s => {
                const isSelected = item.steps.some(st => st.sub_type === s.sub_type && st.main_type === selectedWf);
                return (
                  <button key={s.sub_type} onClick={() => toggleStep(s)} data-testid={`toggle-${index}-${s.sub_type}`}
                    className={`relative px-3 py-3 rounded-xl text-center transition-all duration-200 ${isSelected
                      ? `${t.chip} text-white shadow-lg scale-[1.04] ring-2`
                      : s.optional
                        ? `bg-white border border-dashed border-slate-300 text-slate-400 ${t.optHover}`
                        : `bg-white border border-slate-200 text-slate-700 ${t.hover}`}`}>
                    {isSelected && <CheckCircle2 className={`w-3.5 h-3.5 absolute top-1.5 left-1.5 ${t.check}`} />}
                    <p className={`font-bold leading-snug ${isSelected ? 'text-sm text-white' : 'text-xs text-slate-700'}`}>{isRTL ? s.ar : s.en}</p>
                    {s.optional && !isSelected && <p className="text-[8px] text-slate-400 mt-0.5">{isRTL ? 'اختياري' : 'optional'}</p>}
                  </button>
                );
              })}
            </div>
          </div>
          ); })()}

          {/* Selected steps with prices */}
          {item.steps.length > 0 && (
            (() => { const t = getStepTheme(selectedWf); return (
            <div className={`space-y-1 rounded-2xl p-3 border ${t.wrap}`}>
              {item.steps.map((step, idx) => (
                <div key={idx} data-testid={`price-row-${index}-${idx}`}
                  className={`flex items-center py-1.5 px-2 bg-white rounded-lg border ${t.row} hover:shadow-sm transition-shadow`}>
                  <div className={`w-1 h-6 rounded-full ${t.bar} shrink-0`} />
                  <p className="text-sm text-slate-800 font-bold truncate mx-2">{isRTL ? step.label_ar : step.label_en}</p>
                  <div className={`flex items-center gap-1 bg-slate-50 rounded-lg px-1.5 py-0.5 border border-slate-200 ${isRTL ? 'mr-auto' : 'ml-auto'}`}>
                    <Input type="number" value={step.price || ''} onChange={e => updatePrice(idx, e.target.value)}
                      placeholder="0" dir="ltr" className="w-24 h-7 text-sm text-center font-bold border-0 bg-transparent shadow-none focus-visible:ring-0 p-0" />
                    <span className="text-[9px] text-slate-400 font-medium">{isRTL ? 'د.إ' : 'AED'}</span>
                  </div>
                  <button onClick={() => removeStep(idx)} className={`p-1 hover:bg-red-50 rounded-lg text-slate-300 hover:text-red-500 transition-colors ${isRTL ? 'mr-1' : 'ml-1'}`}>
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {/* Custom step */}
              <div className="flex items-center gap-2 px-3 py-2 bg-slate-50/50 rounded-xl border border-dashed border-slate-200">
                <Plus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <Input value={customStepName} onChange={e => setCustomStepName(e.target.value)}
                  placeholder={isRTL ? 'خطوة مخصصة...' : 'Custom step...'}
                  className="h-6 text-[11px] flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 p-0"
                  onKeyDown={e => { if (e.key === 'Enter') addCustomStep(); }} />
                {customStepName.trim() && (
                  <Button size="sm" variant="ghost" onClick={addCustomStep} className={`h-6 text-[10px] ${getStepTheme(selectedWf).addTxt} ${getStepTheme(selectedWf).addBg}`}>{isRTL ? 'إضافة' : 'Add'}</Button>
                )}
              </div>
            </div>
            ); })()
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// MAIN
// ═══════════════════════════════════════════
const CreatePlan = ({ isRTL, employees, headers, onClose, onCreated }) => {
  const [planItems, setPlanItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState('');
  const [showSearch, setShowSearch] = useState(true);
  const [replacingIndex, setReplacingIndex] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [clientBalance, setClientBalance] = useState(null);
  const [clientId, setClientId] = useState('');
  const [clientName, setClientName] = useState('');
  const [executeMode, setExecuteMode] = useState('auto'); // 'auto' | 'on_account'

  useEffect(() => {
    axios.get(`${API}/companies`, { headers }).then(r => setCompanies(r.data || [])).catch(() => {});
  }, []);

  const selectedIds = new Set(planItems.map(i => i.entity?.id));
  const grandTotal = planItems.reduce((s, item) => s + item.steps.reduce((ss, st) => ss + (parseFloat(st.price) || 0), 0), 0);
  const validCount = planItems.filter(i => i.steps.length > 0).length;
  const currentStep = planItems.length === 0 ? 0 : !planItems.some(i => i.selectedWf) ? 1 : !planItems.some(i => i.steps.length > 0) ? 2 : 3;

  const addEntity = async (entity, type) => {
    let existingPlans = null;
    if (type === 'employee') {
      try {
        const res = await axios.get(`${API}/work-plans/check-employee/${entity.id}`, { headers });
        if (res.data.active?.length > 0 || res.data.archived?.length > 0) existingPlans = res.data;
      } catch {}
      // Fetch client balance from first employee
      if (!clientId) {
        try {
          const compId = entity.company_id || entity.establishment_id;
          const comp = companies.find(c => c.id === compId);
          const cId = comp?.client_id;
          if (cId) {
            setClientId(cId);
            const balRes = await axios.get(`${API}/clients/${cId}/balance`, { headers });
            setClientBalance(balRes.data?.balance ?? 0);
          }
        } catch {}
      }
    } else if (type === 'company') {
      if (!clientId && entity.client_id) {
        try {
          setClientId(entity.client_id);
          const balRes = await axios.get(`${API}/clients/${entity.client_id}/balance`, { headers });
          setClientBalance(balRes.data?.balance ?? 0);
        } catch {}
      }
    }
    const newItem = { planType: type, entity, steps: [], existingPlans, selectedWf: null, priceList: {} };
    const priceCompId = type === 'company' ? entity.id : (entity.company_id || entity.establishment_id);
    if (priceCompId) {
      try {
        const pr = await axios.get(`${API}/companies/${priceCompId}/prices`, { headers });
        newItem.priceList = pr.data?.prices || {};
      } catch {}
    }

    if (replacingIndex !== null) {
      setPlanItems(prev => {
        const n = [...prev];
        n.splice(replacingIndex, 0, newItem);
        return n;
      });
      setActiveIndex(replacingIndex);
      setReplacingIndex(null);
    } else {
      setPlanItems(prev => [newItem, ...prev]);
      setActiveIndex(0);
    }
    setShowSearch(false);
  };

  const removeItem = (idx) => {
    setPlanItems(prev => prev.filter((_, i) => i !== idx));
    if (planItems.length <= 1) setShowSearch(true);
  };

  const replaceEmployee = (idx) => {
    setPlanItems(prev => prev.filter((_, i) => i !== idx));
    setReplacingIndex(idx);
    setShowSearch(true);
  };

  const updateItem = (idx, updates) => {
    setPlanItems(prev => prev.map((item, i) => i === idx ? { ...item, ...updates } : item));
  };

  const save = async () => {
    const validItems = planItems.filter(i => i.steps.length > 0);
    if (validItems.length === 0) return toast.error(isRTL ? 'أضف خطوة واحدة على الأقل' : 'Add at least one step');
    setSaving(true);
    try {
      validItems.forEach(item => { if (item.selectedWf) savePrices(item.selectedWf, item.steps); });
      const items = validItems.map(item => ({
        plan_type: item.planType,
        employee_id: item.planType === 'employee' ? item.entity.id : undefined,
        company_id: item.planType === 'company' ? item.entity.id : undefined,
        steps: item.steps,
      }));
      await axios.post(`${API}/work-plans/batch`, { items, notes, execute_mode: executeMode }, { headers });
      toast.success(isRTL ? `تم إنشاء ${validItems.length} خطة بنجاح` : `${validItems.length} plan(s) created`);
      if (onCreated) onCreated(); else onClose();
    } catch (e) { toast.error(e.response?.data?.detail || (isRTL ? 'خطأ في الحفظ' : 'Error saving')); }
    setSaving(false);
  };

  const stepLabels = isRTL
    ? ['اختيار الموظف', 'نوع الخطة', 'الخطوات والأسعار']
    : ['Select Employee', 'Plan Type', 'Steps & Prices'];

  return (
    <div className="max-w-4xl mx-auto space-y-4 pb-8" dir={isRTL ? 'rtl' : 'ltr'} data-testid="create-plan-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-slate-900">{isRTL ? 'خطة عمل جديدة' : 'New Work Plan'}</h2>
          {planItems.length > 0 && (
            <span className="text-xs font-bold bg-[#0F172A] text-white px-2.5 py-1 rounded-full">
              {planItems.length} {isRTL ? (planItems.length === 1 ? 'موظف' : 'موظفين') : 'employee(s)'}
            </span>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></Button>
      </div>

      {/* Step Progress */}
      <div className="flex items-center gap-2 py-2">
        {stepLabels.map((label, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all ${i < currentStep ? 'bg-[#D4AF37] text-[#0F172A]' : i === currentStep ? 'bg-[#0F172A] text-white' : 'bg-slate-200 text-slate-400'}`}>
              {i < currentStep ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </div>
            <span className={`text-[11px] font-medium ${i <= currentStep ? 'text-slate-700' : 'text-slate-400'}`}>{label}</span>
            {i < stepLabels.length - 1 && <div className={`w-6 h-px mx-1 ${i < currentStep ? 'bg-[#D4AF37]' : 'bg-slate-200'}`} />}
          </div>
        ))}
      </div>

      {/* Search (initial or replacing) */}
      {showSearch && (
        <EntitySearch isRTL={isRTL} employees={employees} companies={companies} selectedIds={selectedIds}
          onAdd={addEntity} onCancel={() => { setShowSearch(false); setReplacingIndex(null); }} showCancel={planItems.length > 0} />
      )}

      {/* Staircase Wizards */}
      {planItems.map((item, idx) => {
        const isCollapsed = idx !== activeIndex && item.steps.length > 0;
        let collapsedNum = 0;
        if (isCollapsed) {
          collapsedNum = planItems.filter((p, i) => i !== activeIndex && p.steps.length > 0 && i <= idx).length;
        }
        return (
          <StaircaseWizard key={`${item.entity?.id}-${idx}`} item={item} index={idx} isRTL={isRTL}
            onUpdate={updateItem} onRemove={removeItem} onReplaceEmployee={replaceEmployee}
            canRemove={planItems.length > 1}
            collapsed={isCollapsed}
            collapsedNumber={collapsedNum}
            onExpand={() => setActiveIndex(idx)} />
        );
      })}

      {/* Add another employee */}
      {planItems.length > 0 && !showSearch && (
        <button onClick={() => { setReplacingIndex(null); setShowSearch(true); }} data-testid="add-another-btn"
          className="w-full flex items-center justify-center gap-2 h-11 text-sm font-bold rounded-xl border-2 border-[#0F172A]/20 text-[#0F172A] bg-[#0F172A]/5 hover:bg-[#0F172A]/10 hover:border-[#0F172A]/40 transition-all">
          <UserPlus className="w-4 h-4" /> {isRTL ? 'إضافة خطة لموظف آخر' : 'Add plan for another employee'}
        </button>
      )}

      {/* Notes */}
      {planItems.some(i => i.steps.length > 0) && (
        <Input value={notes} onChange={e => setNotes(e.target.value)} data-testid="plan-notes"
          placeholder={isRTL ? 'ملاحظات (اختياري)...' : 'Notes (optional)...'} className="h-9 text-sm" />
      )}

      {/* Summary & Save */}
      {validCount > 0 && (
        <Card className="overflow-hidden">
          {/* Financial Summary */}
          <div className="px-5 py-4 bg-[#0F172A] space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center">
                <p className="text-[10px] text-white/40 mb-0.5">{isRTL ? 'إجمالي الخطط' : 'Total Plans'}</p>
                <p className="text-lg font-bold text-[#D4AF37]" dir="ltr">
                  {isRTL && <span className="text-[10px]">درهم </span>}
                  {grandTotal.toLocaleString()}
                  {!isRTL && <span className="text-[10px]"> AED</span>}
                </p>
              </div>
              {clientBalance !== null && (
                <div className="text-center">
                  <p className="text-[10px] text-white/40 mb-0.5">{isRTL ? 'رصيد العميل' : 'Client Balance'}</p>
                  <p className={`text-lg font-bold ${clientBalance >= grandTotal ? 'text-emerald-400' : 'text-orange-400'}`} dir="ltr">
                    {isRTL && <span className="text-[10px]">درهم </span>}
                    {clientBalance.toLocaleString()}
                    {!isRTL && <span className="text-[10px]"> AED</span>}
                  </p>
                </div>
              )}
              {clientBalance !== null && (
                <div className="text-center">
                  <p className="text-[10px] text-white/40 mb-0.5">{isRTL ? (clientBalance >= grandTotal ? 'المتبقي بعد الخصم' : 'المبلغ على الحساب') : (clientBalance >= grandTotal ? 'Remaining' : 'On Account')}</p>
                  <p className={`text-lg font-bold ${clientBalance >= grandTotal ? 'text-emerald-400' : 'text-red-400'}`} dir="ltr">
                    {isRTL && <span className="text-[10px]">درهم </span>}
                    {clientBalance >= grandTotal
                      ? (clientBalance - grandTotal).toLocaleString()
                      : (grandTotal - clientBalance).toLocaleString()
                    }
                    {!isRTL && <span className="text-[10px]"> AED</span>}
                  </p>
                </div>
              )}
            </div>

            {/* Execute mode toggle when balance insufficient */}
            {clientBalance !== null && clientBalance < grandTotal && (
              <div className="flex items-center gap-2 pt-1 border-t border-white/10">
                <button onClick={() => setExecuteMode(executeMode === 'on_account' ? 'auto' : 'on_account')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${executeMode === 'on_account' ? 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-400/40' : 'bg-white/5 text-white/50 hover:bg-white/10'}`}>
                  {executeMode === 'on_account' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <div className="w-3.5 h-3.5 rounded-full border border-white/30" />}
                  {isRTL ? 'تنفيذ على الحساب' : 'Execute on Account'}
                </button>
                <span className="text-[10px] text-white/30">{isRTL ? 'الرصيد غير كافي - اختر التنفيذ على الحساب أو أضف رصيد أولاً' : 'Insufficient balance'}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-white/40 bg-white/10 px-2.5 py-1 rounded-full">
                {validCount} {isRTL ? (validCount === 1 ? 'خطة' : 'خطط') : 'plan(s)'} • {planItems.length} {isRTL ? (planItems.length === 1 ? 'موظف' : 'موظفين') : 'emp'}
              </span>
              <Button onClick={save} disabled={saving} data-testid="save-plans-btn"
                className="gap-2 h-10 px-6 text-sm font-bold bg-[#D4AF37] text-[#0F172A] hover:bg-[#c5a033] disabled:opacity-40">
                {saving ? <div className="w-4 h-4 border-2 border-slate-800 border-t-transparent rounded-full animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {isRTL ? 'حفظ الخطط' : 'Save Plans'}
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default CreatePlan;
